// src/pages/problem-bank/AnalystSquadAssignment.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import {
  ArrowLeft,
  Users,
  Mail,
  Calendar,
  Target,
  CheckCircle2,
  Clock,
  Award,
  Star,
  AlertCircle,
  Loader2,
  Plus,
  X,
  Sparkles,
  Lightbulb,
  Building2,
  MapPin,
  TrendingUp,
  BarChart3,
  FileText,
  CheckSquare,
  Edit2,
  Save,
  XCircle,
  Eye,
  ListChecks,
  Package,
  LineChart,
  Search,
  UserCheck,
  DollarSign,
  Shield
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

interface ProblemBankEntry {
  id: string;
  title: string;
  description: string;
  company: string;
  industry: string;
  industry_sector: string;
  category: string[];
  difficulty: string;
  expectedOutcome: string;
  challenges: string[];
  resourcesProvided: string[];
  contactEmail: string;
  commitmentHours: number;
  durationWeeks: number;
  createdAt: string;
  updatedAt: string;
  status: string;
  quantification: any;
  assignment: any;
  priorityScore: any;
  problemId: string;
  anchor?: any;
  verification?: any;
  solutionPotential?: any;
  rootCauseAnalysis?: any;
  analystNotes?: string;
}

interface Squad {
  id: string;
  name: string;
  squadCode: string;
  university: string;
  leadName: string;
  leadEmail: string;
  leadPhone: string;
  status: string;
  members: any[];
  kpiProgress: number;
  business_interests?: any;
  matchScore?: number;
  matchReasons?: string[];
  metrics?: {
    completedProjects: number;
    successRate: number;
    avgCompletionTime: number;
  };
}

interface SquadAssignment {
  id: string;
  problemId: string;
  squadId: string;
  assignedAt: string;
  targetDate?: string;
  status: string;
  notes?: string;
  solutionExpectations?: any;
  analystBrief?: any;
}

interface SquadProfileModalProps {
  squad: Squad | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function SquadProfileModal({ squad, open, onOpenChange }: SquadProfileModalProps) {
  if (!squad) return null;

  const getSectorLabel = (sector: string) => {
    const labels: Record<string, string> = {
      technology_software: 'Technology & Software',
      healthcare_pharma: 'Healthcare & Pharma',
      energy_utilities: 'Energy & Utilities',
      agribusiness: 'Agribusiness',
      manufacturing: 'Manufacturing',
      fintech: 'Fintech'
    };
    return labels[sector] || sector;
  };

  const getRoleLabel = (role: string) => {
    const roles: Record<string, string> = {
      lead: 'Project Lead',
      market_analyst: 'Market Analyst',
      product_lead: 'Product Lead',
      finance_lead: 'Finance Lead'
    };
    return roles[role] || role;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{squad.name}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 mt-1">
            <Building2 className="h-3 w-3" />
            {squad.university} • Squad Code: {squad.squadCode}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Squad Lead
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
                  {squad.leadName?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                  <p className="font-semibold text-lg">{squad.leadName}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    {squad.leadEmail}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members ({squad.members?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {squad.members?.map((member: any) => (
                  <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg border">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-medium text-primary">
                        {member.full_name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{member.full_name}</p>
                      <p className="text-xs text-muted-foreground">{getRoleLabel(member.role)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {squad.business_interests && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5" />
                  Business Interests
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {squad.business_interests?.primarySectors?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Target Industries</p>
                    <div className="flex flex-wrap gap-2">
                      {squad.business_interests.primarySectors.map((sector: string) => (
                        <Badge key={sector} className="bg-blue-50 text-blue-700 border-blue-200">
                          {getSectorLabel(sector)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {squad.business_interests?.problemCategories?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Interested Problem Areas</p>
                    <div className="flex flex-wrap gap-2">
                      {squad.business_interests.problemCategories.slice(0, 5).map((cat: string) => (
                        <Badge key={cat} variant="secondary">
                          {cat.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-primary/5 rounded-lg">
                  <p className="text-2xl font-bold text-primary">
                    {squad.metrics?.completedProjects || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Completed Projects</p>
                </div>
                <div className="text-center p-3 bg-primary/5 rounded-lg">
                  <p className="text-2xl font-bold text-primary">
                    {squad.metrics?.successRate || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Success Rate</p>
                </div>
                <div className="text-center p-3 bg-primary/5 rounded-lg">
                  <p className="text-2xl font-bold text-primary">
                    {squad.kpiProgress || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">KPI Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Calculate match score with detailed reasons based on squad interests
const calculateDetailedMatchScore = (squad: any, problem: ProblemBankEntry): { score: number; reasons: string[] } => {
  let score = 0;
  const reasons: string[] = [];
  
  // Get squad interests
  const primarySectors = squad.business_interests?.primarySectors || [];
  const problemCategories = squad.business_interests?.problemCategories || [];
  
  // Match based on industry sector (30 points)
  if (primarySectors.includes(problem.industry_sector)) {
    score += 30;
    reasons.push(`Industry match: ${problem.industry_sector}`);
  } else if (problem.industry_sector === 'agro_processing' && primarySectors.includes('agribusiness')) {
    score += 30;
    reasons.push('Industry alignment: Agribusiness → Agro-processing');
  }
  
  // Match based on problem categories (20 points each)
  const problemCatList = problem.category || [];
  const matchedCategories = problemCatList.filter((cat: string) => problemCategories.includes(cat));
  score += matchedCategories.length * 20;
  if (matchedCategories.length > 0) {
    reasons.push(`Matches ${matchedCategories.length} problem area(s): ${matchedCategories.join(', ')}`);
  }
  
  return {
    score: Math.min(score, 100),
    reasons: reasons.slice(0, 3)
  };
};

export default function AnalystSquadAssignment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Determine user role type
  const isProblemBankAnalyst = user?.analyst_type === 'problem_bank' || user?.role === 'senior_analyst';
  const isSeniorAnalyst = user?.role === 'senior_analyst';
  const isBusinessAnalyst = user?.analyst_type === 'business';
  
  const [isLoading, setIsLoading] = useState(true);
  const [problem, setProblem] = useState<ProblemBankEntry | null>(null);
  const [availableSquads, setAvailableSquads] = useState<Squad[]>([]);
  const [assignedSquads, setAssignedSquads] = useState<SquadAssignment[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSquadId, setSelectedSquadId] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [activeTab, setActiveTab] = useState<'recommended' | 'all'>('all');
  const [selectedSquad, setSelectedSquad] = useState<Squad | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [squadSearchQuery, setSquadSearchQuery] = useState('');
  
  // Solution expectations editing
  const [isEditingExpectations, setIsEditingExpectations] = useState(false);
  const [solutionExpectations, setSolutionExpectations] = useState({
    deliverables: [''],
    successCriteria: [''],
    milestones: [{ description: '', targetDate: '', completed: false }],
    expectedOutcome: ''
  });
  
  // Analyst Brief fields
  const [analystBrief, setAnalystBrief] = useState({
    challenges: [''],
    resources: [''],
    quantification: '',
    additionalNotes: ''
  });
  const [isEditingBrief, setIsEditingBrief] = useState(true);
  const [isBriefValid, setIsBriefValid] = useState(false);

  // Business Analysts should access this page (they assign problems to squads)
  // Problem Bank Analysts should NOT access this page (they don't handle squad assignments)
  // Senior Analysts have full access
  if (isProblemBankAnalyst && !isSeniorAnalyst) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground text-center max-w-md">
          Squad Assignment is only accessible to Business Analysts and Senior Analysts.
          Problem Bank Analysts focus on problem research and verification.
        </p>
        <Button className="mt-6" onClick={() => navigate('/analyst-dashboard')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  // Validate brief fields
  useEffect(() => {
    const hasValidChallenges = analystBrief.challenges.some(c => c.trim() !== '');
    const hasValidResources = analystBrief.resources.some(r => r.trim() !== '');
    const hasValidQuantification = analystBrief.quantification.trim() !== '';
    setIsBriefValid(hasValidChallenges && hasValidResources && hasValidQuantification);
  }, [analystBrief]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load problem
      const { data: problemData, error: problemError } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('id', id)
        .single();

      if (problemError) throw problemError;

      const parsedProblem: ProblemBankEntry = {
        id: problemData.id,
        title: problemData.title,
        description: problemData.description,
        company: problemData.company,
        industry: problemData.industry,
        industry_sector: problemData.industry_sector,
        category: problemData.category || [],
        difficulty: problemData.difficulty,
        expectedOutcome: problemData.expected_outcome,
        challenges: problemData.challenges || [],
        resourcesProvided: problemData.resources_provided || [],
        contactEmail: problemData.contact_email,
        commitmentHours: problemData.commitment_hours,
        durationWeeks: problemData.duration_weeks,
        createdAt: problemData.created_at,
        updatedAt: problemData.updated_at,
        status: problemData.status,
        quantification: typeof problemData.quantification === 'string' ? JSON.parse(problemData.quantification) : problemData.quantification,
        assignment: typeof problemData.assignment === 'string' ? JSON.parse(problemData.assignment || '{}') : problemData.assignment,
        priorityScore: problemData.priority_score,
        problemId: problemData.problem_id,
        anchor: typeof problemData.anchor === 'string' ? JSON.parse(problemData.anchor) : problemData.anchor,
        verification: typeof problemData.verification === 'string' ? JSON.parse(problemData.verification) : problemData.verification,
        solutionPotential: typeof problemData.solution_potential === 'string' ? JSON.parse(problemData.solution_potential) : problemData.solution_potential,
        rootCauseAnalysis: typeof problemData.root_cause_analysis === 'string' ? JSON.parse(problemData.root_cause_analysis) : problemData.root_cause_analysis,
        analystNotes: problemData.analyst_notes
      };
      setProblem(parsedProblem);

      // Load active squads with members and leads
      const { data: squadsData, error: squadsError } = await supabase
        .from('squad_registrations')
        .select(`
          id,
          squad_name,
          squad_code,
          university,
          status,
          business_interests,
          lead:squad_members!squad_members_squad_id_fkey (
            full_name,
            email,
            phone,
            role
          ),
          members:squad_members!squad_members_squad_id_fkey (
            id,
            full_name,
            role,
            email,
            phone
          )
        `)
        .eq('status', 'active');

      if (squadsError) {
        console.error('Error loading squads:', squadsError);
      }

      const processedSquads: Squad[] = [];

      if (squadsData && squadsData.length > 0) {
        for (const squad of squadsData) {
          // Get lead info (filter where role = 'lead')
          const leadMember = squad.lead?.find((m: any) => m.role === 'lead');
          
          // Get all members
          const allMembers = squad.members || [];
          
          if (allMembers.length > 0) {
            const matchResult = calculateDetailedMatchScore(squad, parsedProblem);
            processedSquads.push({
              id: squad.id,
              name: squad.squad_name,
              squadCode: squad.squad_code,
              university: squad.university || 'University',
              leadName: leadMember?.full_name || 'Unknown',
              leadEmail: leadMember?.email || '',
              leadPhone: leadMember?.phone || '',
              status: squad.status,
              members: allMembers,
              kpiProgress: 0,
              business_interests: squad.business_interests,
              matchScore: matchResult.score,
              matchReasons: matchResult.reasons,
              metrics: {
                completedProjects: 0,
                successRate: 0,
                avgCompletionTime: 0
              }
            });
          }
        }
      }

      const sortedSquads = processedSquads.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
      setAvailableSquads(sortedSquads);

      // Load existing assignments
      const { data: assignmentsData } = await supabase
        .from('problem_assignments')
        .select('*')
        .eq('problem_id', id);

      if (assignmentsData) {
        setAssignedSquads(assignmentsData);
      }

      // Load existing solution expectations
      if (parsedProblem.assignment?.solutionExpectations) {
        setSolutionExpectations(parsedProblem.assignment.solutionExpectations);
      }

      // Load existing analyst brief
      if (parsedProblem.assignment?.analystBrief) {
        setAnalystBrief(parsedProblem.assignment.analystBrief);
        setIsEditingBrief(false);
      } else {
        setAnalystBrief({
          challenges: parsedProblem.challenges?.length ? parsedProblem.challenges : [''],
          resources: parsedProblem.resourcesProvided?.length ? parsedProblem.resourcesProvided : [''],
          quantification: parsedProblem.quantification?.description || '',
          additionalNotes: parsedProblem.analystNotes || ''
        });
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load problem data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAnalystBrief = () => {
    if (!isBriefValid) {
      toast({
        title: 'Incomplete Brief',
        description: 'Please fill in at least one challenge, one resource, and quantification.',
        variant: 'destructive',
      });
      return;
    }
    setIsEditingBrief(false);
    toast({
      title: 'Brief Saved',
      description: 'Analyst brief has been saved.',
    });
  };

  const handleSaveSolutionExpectations = async () => {
    if (!problem) return;
    
    try {
      const cleanedExpectations = {
        deliverables: solutionExpectations.deliverables.filter(d => d.trim() !== ''),
        successCriteria: solutionExpectations.successCriteria.filter(c => c.trim() !== ''),
        milestones: solutionExpectations.milestones.filter(m => m.description.trim() !== ''),
        expectedOutcome: solutionExpectations.expectedOutcome
      };
      
      const updatedAssignment = {
        ...problem.assignment,
        solutionExpectations: cleanedExpectations
      };
      
      const { error } = await supabase
        .from('problem_bank')
        .update({
          assignment: updatedAssignment,
          updated_at: new Date().toISOString()
        })
        .eq('id', problem.id);
      
      if (error) throw error;
      
      setSolutionExpectations(cleanedExpectations);
      
      toast({
        title: 'Saved',
        description: 'Solution expectations updated successfully',
      });
      setIsEditingExpectations(false);
    } catch (error) {
      console.error('Error saving expectations:', error);
      toast({
        title: 'Error',
        description: 'Failed to save expectations',
        variant: 'destructive',
      });
    }
  };

  const handleAssignSquad = async () => {
    if (!selectedSquadId || !problem) return;
    
    if (!isBriefValid) {
      toast({
        title: 'Complete Analyst Brief First',
        description: 'Please fill in challenges, resources, and quantification before assigning.',
        variant: 'destructive',
      });
      return;
    }

    setIsAssigning(true);
    try {
      const selectedSquadData = availableSquads.find(s => s.id === selectedSquadId);
      
      const newAssignment = {
        problem_id: problem.id,
        squad_id: selectedSquadId,
        assigned_at: new Date().toISOString(),
        target_date: targetDate || null,
        status: 'pending',
        notes: assignmentNotes || null,
        solution_expectations: solutionExpectations,
        analyst_brief: {
          challenges: analystBrief.challenges.filter(c => c.trim() !== ''),
          resources: analystBrief.resources.filter(r => r.trim() !== ''),
          quantification: analystBrief.quantification,
          additionalNotes: analystBrief.additionalNotes
        }
      };
      
      const { error } = await supabase
        .from('problem_assignments')
        .insert(newAssignment);
      
      if (error) throw error;
      
      // Update problem status
      await supabase
        .from('problem_bank')
        .update({ 
          status: 'assigned',
          assigned_to_squad_id: selectedSquadId,
          assigned_to_squad_name: selectedSquadData?.name,
          assigned_at: new Date().toISOString()
        })
        .eq('id', problem.id);

      toast({
        title: 'Squad Assigned',
        description: `Problem has been assigned to ${selectedSquadData?.name} successfully.`,
      });

      setAssignDialogOpen(false);
      setSelectedSquadId('');
      setAssignmentNotes('');
      setTargetDate('');
      loadData();
    } catch (error) {
      console.error('Error assigning squad:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign squad',
        variant: 'destructive',
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    try {
      const { error } = await supabase
        .from('problem_assignments')
        .delete()
        .eq('id', assignmentId);
      
      if (error) throw error;
      
      const updatedAssignments = assignedSquads.filter(a => a.id !== assignmentId);
      setAssignedSquads(updatedAssignments);
      
      if (updatedAssignments.length === 0 && problem) {
        await supabase
          .from('problem_bank')
          .update({ status: 'available' })
          .eq('id', problem.id);
      }

      toast({
        title: 'Assignment Removed',
        description: 'Squad has been unassigned from this problem.',
      });
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove assignment',
        variant: 'destructive',
      });
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'SQ';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Brief field handlers
  const handleAddChallenge = () => {
    setAnalystBrief(prev => ({ ...prev, challenges: [...prev.challenges, ''] }));
  };

  const handleRemoveChallenge = (index: number) => {
    setAnalystBrief(prev => ({
      ...prev,
      challenges: prev.challenges.filter((_, i) => i !== index)
    }));
  };

  const handleChallengeChange = (index: number, value: string) => {
    setAnalystBrief(prev => ({
      ...prev,
      challenges: prev.challenges.map((c, i) => i === index ? value : c)
    }));
  };

  const handleAddResource = () => {
    setAnalystBrief(prev => ({ ...prev, resources: [...prev.resources, ''] }));
  };

  const handleRemoveResource = (index: number) => {
    setAnalystBrief(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const handleResourceChange = (index: number, value: string) => {
    setAnalystBrief(prev => ({
      ...prev,
      resources: prev.resources.map((r, i) => i === index ? value : r)
    }));
  };

  // Solution expectations handlers
  const handleAddDeliverable = () => {
    setSolutionExpectations(prev => ({ ...prev, deliverables: [...prev.deliverables, ''] }));
  };

  const handleRemoveDeliverable = (index: number) => {
    setSolutionExpectations(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }));
  };

  const handleDeliverableChange = (index: number, value: string) => {
    setSolutionExpectations(prev => ({
      ...prev,
      deliverables: prev.deliverables.map((d, i) => i === index ? value : d)
    }));
  };

  const handleAddSuccessCriteria = () => {
    setSolutionExpectations(prev => ({ ...prev, successCriteria: [...prev.successCriteria, ''] }));
  };

  const handleRemoveSuccessCriteria = (index: number) => {
    setSolutionExpectations(prev => ({
      ...prev,
      successCriteria: prev.successCriteria.filter((_, i) => i !== index)
    }));
  };

  const handleSuccessCriteriaChange = (index: number, value: string) => {
    setSolutionExpectations(prev => ({
      ...prev,
      successCriteria: prev.successCriteria.map((c, i) => i === index ? value : c)
    }));
  };

  const handleAddMilestone = () => {
    setSolutionExpectations(prev => ({
      ...prev,
      milestones: [...prev.milestones, { description: '', targetDate: '', completed: false }]
    }));
  };

  const handleRemoveMilestone = (index: number) => {
    setSolutionExpectations(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  const handleMilestoneChange = (index: number, field: string, value: string) => {
    setSolutionExpectations(prev => ({
      ...prev,
      milestones: prev.milestones.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };

  const filteredSquads = availableSquads.filter(squad =>
    squad.name.toLowerCase().includes(squadSearchQuery.toLowerCase()) ||
    squad.university.toLowerCase().includes(squadSearchQuery.toLowerCase())
  );

  const recommendedSquads = filteredSquads.filter(s => (s.matchScore || 0) >= 40);
  const allFilteredSquads = filteredSquads;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3">Loading...</span>
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Problem not found</AlertDescription>
        </Alert>
        <Button className="mt-4" onClick={() => navigate('/analyst-dashboard')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const selectedSquadData = availableSquads.find(s => s.id === selectedSquadId);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* FULL WIDTH - No max-w constraints */}
      <div className="w-full px-4 py-4 md:px-6 lg:px-8">
        {/* Simple Back Button */}
        <div className="mb-4">
          <Button variant="ghost" onClick={() => navigate('/analyst-dashboard')} size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

        {/* Problem Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold break-words">{problem.title}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              <span className="break-words">{problem.anchor?.name || problem.company || 'N/A'}</span>
            </div>
            <span className="text-muted-foreground">•</span>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span className="break-words">{problem.anchor?.location || 'N/A'}</span>
            </div>
            <Badge className="bg-blue-100 text-blue-700">
              {problem.difficulty || 'Intermediate'}
            </Badge>
          </div>
        </div>

        {/* Problem Description Card */}
        <Card className="mb-6 border-l-4 border-l-blue-500">
          <CardHeader className="bg-muted/30 py-3">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-base">Problem Description</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-muted-foreground whitespace-pre-wrap break-words">
              {problem.description || 'No description provided.'}
            </p>
          </CardContent>
        </Card>

        {/* Two Column Layout - Full width */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Quantification Card */}
            <Card className="border-l-4 border-l-red-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-red-600" />
                  <CardTitle className="text-base">Quantification</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Annual Loss</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(problem.quantification?.totalLossPerYear || 0)}
                  </p>
                </div>
                {problem.quantification?.description && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium mb-2">Description</p>
                      <p className="text-sm text-muted-foreground break-words">{problem.quantification.description}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Priority Score Card */}
            {problem.priorityScore && (
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader className="bg-muted/30 py-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-orange-600" />
                    <CardTitle className="text-base">Priority Score</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <span className="text-sm font-medium">Overall Score</span>
                    <Badge className={problem.priorityScore.priorityTier === 'critical' ? 'bg-red-100 text-red-700' : problem.priorityScore.priorityTier === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}>
                      {problem.priorityScore.totalScore?.toFixed(1)}/5 - {problem.priorityScore.priorityTier?.toUpperCase()}
                    </Badge>
                  </div>
                  <Progress value={(problem.priorityScore.totalScore / 5) * 100} className="h-2" />
                </CardContent>
              </Card>
            )}

            {/* Analyst Brief Section */}
            <Card className={!isBriefValid && !isEditingBrief ? 'border-red-300 bg-red-50/30' : 'border-l-4 border-l-purple-500'}>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  <CardTitle className="text-base">Analyst Brief</CardTitle>
                </div>
                {!isEditingBrief ? (
                  <Button variant="outline" size="sm" onClick={() => setIsEditingBrief(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setIsEditingBrief(false)}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveAnalystBrief}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Brief
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                <CardDescription className="text-xs">Required fields before assigning to squad</CardDescription>
                
                {/* Key Challenges */}
                <div>
                  <Label className="font-medium flex items-center gap-2 text-sm">
                    <ListChecks className="h-4 w-4" />
                    Key Challenges *
                  </Label>
                  <div className="space-y-2 mt-2">
                    {analystBrief.challenges.map((challenge, idx) => (
                      challenge && challenge.trim() !== '' && (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg break-words">
                          <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0" />
                          <span className="text-sm">{challenge}</span>
                        </div>
                      )
                    ))}
                    {isEditingBrief && (
                      <div className="space-y-2">
                        {analystBrief.challenges.map((challenge, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={challenge}
                              onChange={(e) => handleChallengeChange(idx, e.target.value)}
                              placeholder={`Challenge ${idx + 1}`}
                              className="flex-1"
                            />
                            <Button variant="ghost" size="icon" onClick={() => handleRemoveChallenge(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={handleAddChallenge}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Challenge
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resources Provided */}
                <div>
                  <Label className="font-medium flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4" />
                    Resources Provided *
                  </Label>
                  <div className="space-y-2 mt-2">
                    {analystBrief.resources.map((resource, idx) => (
                      resource && resource.trim() !== '' && (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg break-words">
                          <Package className="h-4 w-4 text-blue-500 shrink-0" />
                          <span className="text-sm">{resource}</span>
                        </div>
                      )
                    ))}
                    {isEditingBrief && (
                      <div className="space-y-2">
                        {analystBrief.resources.map((resource, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={resource}
                              onChange={(e) => handleResourceChange(idx, e.target.value)}
                              placeholder={`Resource ${idx + 1}`}
                              className="flex-1"
                            />
                            <Button variant="ghost" size="icon" onClick={() => handleRemoveResource(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={handleAddResource}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Resource
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quantification Summary */}
                <div>
                  <Label className="font-medium flex items-center gap-2 text-sm">
                    <LineChart className="h-4 w-4" />
                    Quantification Summary *
                  </Label>
                  {isEditingBrief ? (
                    <Textarea
                      value={analystBrief.quantification}
                      onChange={(e) => setAnalystBrief(prev => ({ ...prev, quantification: e.target.value }))}
                      placeholder="Summarize the quantified impact, loss, and potential savings..."
                      className="mt-2"
                      rows={4}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground mt-2 bg-muted/30 p-3 rounded-lg break-words">
                      {analystBrief.quantification || 'Not specified'}
                    </p>
                  )}
                </div>

                {!isBriefValid && !isEditingBrief && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please complete all required fields (challenges, resources, and quantification) before assigning to a squad.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Solution Expectations Section */}
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  <CardTitle className="text-base">Solution Expectations</CardTitle>
                </div>
                {!isEditingExpectations ? (
                  <Button variant="outline" size="sm" onClick={() => setIsEditingExpectations(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setIsEditingExpectations(false)}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveSolutionExpectations}>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                <CardDescription className="text-xs">Define what the squad should deliver</CardDescription>
                
                {/* Expected Outcome */}
                <div>
                  <Label className="font-medium text-sm">Expected Outcome</Label>
                  {isEditingExpectations ? (
                    <Textarea
                      value={solutionExpectations.expectedOutcome}
                      onChange={(e) => setSolutionExpectations(prev => ({ ...prev, expectedOutcome: e.target.value }))}
                      className="mt-2"
                      rows={3}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground mt-2 bg-muted/30 p-3 rounded-lg break-words">
                      {solutionExpectations.expectedOutcome || 'Not defined yet'}
                    </p>
                  )}
                </div>

                {/* Deliverables */}
                <div>
                  <Label className="font-medium text-sm">Deliverables</Label>
                  <div className="space-y-2 mt-2">
                    {solutionExpectations.deliverables.map((deliverable, idx) => (
                      deliverable && deliverable.trim() !== '' && (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg break-words">
                          <CheckSquare className="h-4 w-4 text-green-600 shrink-0" />
                          <span className="text-sm">{deliverable}</span>
                        </div>
                      )
                    ))}
                    {isEditingExpectations && (
                      <div className="space-y-2">
                        {solutionExpectations.deliverables.map((deliverable, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={deliverable}
                              onChange={(e) => handleDeliverableChange(idx, e.target.value)}
                              placeholder={`Deliverable ${idx + 1}`}
                              className="flex-1"
                            />
                            <Button variant="ghost" size="icon" onClick={() => handleRemoveDeliverable(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={handleAddDeliverable}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Deliverable
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Success Criteria */}
                <div>
                  <Label className="font-medium text-sm">Success Criteria</Label>
                  <div className="space-y-2 mt-2">
                    {solutionExpectations.successCriteria.map((criteria, idx) => (
                      criteria && criteria.trim() !== '' && (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg break-words">
                          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                          <span className="text-sm">{criteria}</span>
                        </div>
                      )
                    ))}
                    {isEditingExpectations && (
                      <div className="space-y-2">
                        {solutionExpectations.successCriteria.map((criteria, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={criteria}
                              onChange={(e) => handleSuccessCriteriaChange(idx, e.target.value)}
                              placeholder={`Criteria ${idx + 1}`}
                              className="flex-1"
                            />
                            <Button variant="ghost" size="icon" onClick={() => handleRemoveSuccessCriteria(idx)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={handleAddSuccessCriteria}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Success Criteria
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Milestones */}
                <div>
                  <Label className="font-medium text-sm">Milestones</Label>
                  <div className="space-y-3 mt-2">
                    {solutionExpectations.milestones.map((milestone, idx) => (
                      milestone.description && milestone.description.trim() !== '' && (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 shrink-0">
                              {milestone.completed ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <Clock className="h-4 w-4 text-yellow-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium break-words">{milestone.description}</p>
                              {milestone.targetDate && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Target: {new Date(milestone.targetDate).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                    {isEditingExpectations && (
                      <div className="space-y-3">
                        {solutionExpectations.milestones.map((milestone, idx) => (
                          <div key={idx} className="p-3 border rounded-lg space-y-2">
                            <Input
                              value={milestone.description}
                              onChange={(e) => handleMilestoneChange(idx, 'description', e.target.value)}
                              placeholder="Milestone description"
                            />
                            <Input
                              type="date"
                              value={milestone.targetDate}
                              onChange={(e) => handleMilestoneChange(idx, 'targetDate', e.target.value)}
                              placeholder="Target date"
                            />
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveMilestone(idx)} className="text-red-600">
                              <X className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={handleAddMilestone}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Milestone
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Squads */}
          <div className="space-y-6">
            {/* Assigned Squads Section */}
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <CardTitle className="text-base">Assigned Squads</CardTitle>
                </div>
                <CardDescription className="text-xs">Squads currently working on this problem</CardDescription>
              </CardHeader>
              <CardContent>
                {assignedSquads.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No squads assigned yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Complete the analyst brief above and assign a squad</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignedSquads.map((assignment) => {
                      const squad = availableSquads.find(s => s.id === assignment.squadId);
                      if (!squad) return null;
                      return (
                        <div key={assignment.id} className="p-3 border rounded-lg border-l-4 border-l-green-500">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                                  {getInitials(squad.name)}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium truncate">{squad.name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{squad.university}</p>
                                </div>
                              </div>
                              {assignment.targetDate && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Target: {new Date(assignment.targetDate).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge className="bg-yellow-100 text-yellow-700">
                                {assignment.status}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveAssignment(assignment.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available Squads Section */}
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-base">Available Squads</CardTitle>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <CardDescription className="text-xs">Squads available for assignment</CardDescription>
                    <div className="flex flex-wrap gap-2">
                      <div className="relative w-56">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search squads..."
                          value={squadSearchQuery}
                          onChange={(e) => setSquadSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <Button 
                        variant={activeTab === 'recommended' ? 'default' : 'outline'} 
                        size="sm"
                        onClick={() => setActiveTab('recommended')}
                      >
                        <Sparkles className="h-3 w-3 mr-1" />
                        Recommended
                      </Button>
                      <Button 
                        variant={activeTab === 'all' ? 'default' : 'outline'} 
                        size="sm"
                        onClick={() => setActiveTab('all')}
                      >
                        All Squads
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {availableSquads.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">No active squads available</p>
                    <p className="text-sm text-muted-foreground mt-1">Ensure squads have members and are active in the database</p>
                  </div>
                ) : (activeTab === 'recommended' ? recommendedSquads : allFilteredSquads).length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-muted-foreground">
                      {activeTab === 'recommended' ? 'No recommended squads found' : 'No squads match your search'}
                    </p>
                  </div>
                ) : (
                  (activeTab === 'recommended' ? recommendedSquads : allFilteredSquads).map((squad) => (
                    <div 
                      key={squad.id} 
                      className={`p-4 border rounded-lg transition-all hover:shadow-md ${
                        (squad.matchScore || 0) >= 70 ? 'border-green-300 bg-green-50/30' : 
                        (squad.matchScore || 0) >= 40 ? 'border-blue-300 bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row items-start gap-3">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold shrink-0">
                          {getInitials(squad.name)}
                        </div>
                        <div className="flex-1 min-w-0 w-full">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-semibold truncate">{squad.name}</h3>
                              <p className="text-xs text-muted-foreground truncate">{squad.university}</p>
                            </div>
                            {squad.matchScore && squad.matchScore > 0 && (
                              <Badge className={squad.matchScore >= 70 ? 'bg-green-100 text-green-700 shrink-0' : squad.matchScore >= 40 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>
                                {squad.matchScore}% Match
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-3 mt-2">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Users className="h-3 w-3 shrink-0" />
                              <span>{squad.members?.length || 0} members</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
                              <Mail className="h-3 w-3 shrink-0" />
                              <span className="truncate">{squad.leadEmail}</span>
                            </div>
                          </div>

                          {squad.matchReasons && squad.matchReasons.length > 0 && (
                            <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                              <p className="text-xs font-medium text-blue-700 mb-1">Why this squad matches:</p>
                              {squad.matchReasons.map((reason, idx) => (
                                <div key={idx} className="flex items-center gap-1 text-xs text-blue-600">
                                  <Sparkles className="h-3 w-3 shrink-0" />
                                  <span className="break-words">{reason}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedSquad(squad);
                                setProfileModalOpen(true);
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View Profile
                            </Button>
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="bg-blue-600 hover:bg-blue-700"
                              disabled={!isBriefValid}
                              onClick={() => {
                                if (!isBriefValid) {
                                  toast({
                                    title: 'Complete Analyst Brief First',
                                    description: 'Please fill in challenges, resources, and quantification.',
                                    variant: 'destructive',
                                  });
                                  return;
                                }
                                setSelectedSquadId(squad.id);
                                setAssignDialogOpen(true);
                              }}
                            >
                              <Target className="h-3 w-3 mr-1" />
                              Assign
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Squad to Problem</DialogTitle>
            <DialogDescription>
              Assign a squad to work on this problem. They will be notified and can start working.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Selected Squad</Label>
              <div className="p-3 bg-muted rounded-lg">
                {selectedSquadData && (
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {getInitials(selectedSquadData.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{selectedSquadData.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{selectedSquadData.university}</p>
                    </div>
                    {selectedSquadData.matchScore && selectedSquadData.matchScore >= 40 && (
                      <Badge className={selectedSquadData.matchScore >= 70 ? 'bg-green-100 text-green-700 ml-auto shrink-0' : 'bg-blue-100 text-blue-700 ml-auto'}>
                        {selectedSquadData.matchScore}% Match
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Target Completion Date (Optional)</Label>
              <Input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground">
                Recommended: 30-45 days from assignment
              </p>
            </div>

            <div className="space-y-2">
              <Label>Assignment Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes or instructions for the squad..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                rows={3}
              />
            </div>

            {selectedSquadData && selectedSquadData.matchScore && selectedSquadData.matchScore >= 40 && (
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 shrink-0" />
                  <span>This squad has a {selectedSquadData.matchScore}% match with this problem!</span>
                </p>
                {selectedSquadData.matchReasons && selectedSquadData.matchReasons.length > 0 && (
                  <ul className="mt-2 text-xs text-blue-600 list-disc list-inside">
                    {selectedSquadData.matchReasons.slice(0, 2).map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignSquad} 
              disabled={!selectedSquadId || isAssigning}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Target className="mr-2 h-4 w-4" />
                  Assign Squad
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Squad Profile Modal */}
      <SquadProfileModal
        squad={selectedSquad}
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
      />
    </div>
  );
}